<script setup>
import { ref, computed } from 'vue'
import { store } from '@/store/appStore.js'
import { TERRITORIES } from '@/data/mockData.js'

const activeTab = ref('users')

// --- Tab 1: User Management --------------------------------------------
// Session-only mock user directory. The app doesn't have a real auth/user
// backend — role identity is chosen via the persona picker (RoleSelect.vue),
// not per-user login — so this is a self-contained CRUD demo of the
// blueprint's ask, not wired into actual access control (that's the RBAC
// Configurator tab below, which has a real effect).
const seedUsers = () => {
  const names = [...new Set(store.data.pharmacies.value.map(p => p.assignedSalesExec))]
  return names.slice(0, 6).map((name, i) => ({
    id: `U-${1000+i}`, name, role: 'salesExec', territory: TERRITORIES[i % TERRITORIES.length],
  }))
}
const users = ref(seedUsers())
const toast = ref(null)
const notify = msg => { toast.value = msg; setTimeout(() => { toast.value = null }, 2800) }

const showAddUser = ref(false)
const newUser = ref({ name: '', role: 'salesExec', territory: 'Harare' })
function addUser() {
  if (!newUser.value.name.trim()) return
  users.value.push({ id: `U-${1000+users.value.length}`, ...newUser.value })
  notify(`${newUser.value.name} added.`)
  newUser.value = { name: '', role: 'salesExec', territory: 'Harare' }
  showAddUser.value = false
}
function removeUser(id) {
  users.value = users.value.filter(u => u.id !== id)
  notify('User removed.')
}

// --- Tab 2: Master Data Management --------------------------------------
const territoryMaster = computed(() => {
  const byTerr = {}
  store.data.pharmacies.value.forEach(p => { byTerr[p.territory] = (byTerr[p.territory]||0) + 1 })
  const latestPobByTerr = {}
  store.data.pob.value.forEach(r => { if (!latestPobByTerr[r.territory] || r.month > latestPobByTerr[r.territory].month) latestPobByTerr[r.territory] = r })
  return TERRITORIES.map(t => ({ territory: t, pharmacyCount: byTerr[t]||0, latestTarget: latestPobByTerr[t]?.targetValue || 0 }))
})

// --- Tab 3: RBAC Configurator Matrix -------------------------------------
// Toggling a checkbox has a REAL effect: store.visibleModules consults
// rbacOverrides, so this genuinely changes what that role can navigate to —
// not a cosmetic grid.
const roles = store.ROLES
const modules = store.MODULES
</script>

<template>
  <div>
    <div class="card module-header"><span class="eyebrow">Executive Administration Workspace</span><h2>Admin Control Center</h2></div>

    <div v-if="toast" style="position:fixed;bottom:24px;right:24px;background:var(--color-primary-dark);color:#fff;padding:10px 16px;border-radius:8px;font-size:13px;box-shadow:var(--shadow-lg);max-width:360px;z-index:120;">{{ toast }}</div>

    <div class="card" style="padding:0;margin-bottom:16px;">
      <div style="display:flex;border-bottom:1px solid var(--color-border);">
        <button v-for="t in [{id:'users',label:'User Management'},{id:'masterdata',label:'Master Data Management'},{id:'rbac',label:'RBAC Configurator'}]" :key="t.id"
                @click="activeTab=t.id"
                :style="'padding:14px 20px;font-size:13px;font-weight:600;border-bottom:2px solid ' + (activeTab===t.id?'var(--color-primary-dark)':'transparent') + ';color:' + (activeTab===t.id?'var(--color-primary-dark)':'var(--color-text-muted)')">
          {{ t.label }}
        </button>
      </div>

      <!-- Tab 1: User Management -->
      <div v-if="activeTab==='users'" style="padding:20px;">
        <p class="muted" style="font-size:12px;margin:0 0 14px;">Session-only mock directory — this app doesn't have real per-user login; identity is chosen via the role picker. Real access control is the RBAC Configurator tab.</p>
        <button class="btn-primary" style="margin-bottom:14px;" @click="showAddUser=!showAddUser">{{ showAddUser?'Cancel':'+ Add User' }}</button>
        <div v-if="showAddUser" class="oos-remarks" style="margin-bottom:16px;">
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr auto;gap:10px;align-items:end;">
            <div class="form-group" style="margin:0;"><label>Name</label><input class="form-input" v-model="newUser.name" /></div>
            <div class="form-group" style="margin:0;"><label>Role</label>
              <select class="form-select" v-model="newUser.role"><option v-for="r in roles" :key="r.id" :value="r.id">{{ r.label }}</option></select>
            </div>
            <div class="form-group" style="margin:0;"><label>Territory</label>
              <select class="form-select" v-model="newUser.territory"><option v-for="t in TERRITORIES" :key="t">{{ t }}</option></select>
            </div>
            <button class="btn-primary" @click="addUser">Add</button>
          </div>
        </div>
        <table class="data-table">
          <thead><tr><th>ID</th><th>Name</th><th>Role</th><th>Territory</th><th></th></tr></thead>
          <tbody>
            <tr v-for="u in users" :key="u.id">
              <td class="cell-muted">{{ u.id }}</td><td class="cell-strong">{{ u.name }}</td>
              <td class="cell-muted">{{ roles.find(r=>r.id===u.role)?.label }}</td><td class="cell-muted">{{ u.territory }}</td>
              <td><button class="filter-select" style="cursor:pointer;color:var(--status-red);font-size:11px;" @click="removeUser(u.id)">Remove</button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Tab 2: Master Data Management -->
      <div v-if="activeTab==='masterdata'" style="padding:20px;">
        <div class="stat-row">
          <div class="card stat-card"><div class="stat-value">{{ store.data.pharmacies.value.length }}</div><div class="stat-label">Pharmacy Master records</div></div>
          <div class="card stat-card"><div class="stat-value">{{ store.data.products.value.length }}</div><div class="stat-label">Product Master records</div></div>
          <div class="card stat-card"><div class="stat-value">{{ TERRITORIES.length }}</div><div class="stat-label">Territories</div></div>
        </div>
        <p class="muted" style="font-size:12px;margin:16px 0 10px;">Pharmacy and Product Master data is maintained in their own modules (left nav). Territory Master, derived here:</p>
        <table class="data-table">
          <thead><tr><th>Territory</th><th>Pharmacies</th><th>Latest Monthly POB Target</th></tr></thead>
          <tbody>
            <tr v-for="t in territoryMaster" :key="t.territory">
              <td class="cell-strong">{{ t.territory }}</td><td class="cell-muted">{{ t.pharmacyCount }}</td><td class="cell-muted">${{ t.latestTarget.toLocaleString() }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Tab 3: RBAC Configurator Matrix -->
      <div v-if="activeTab==='rbac'" style="padding:20px;">
        <p class="muted" style="font-size:12px;margin:0 0 14px;">Toggling a checkbox genuinely changes what that role can navigate to. Admin's own access to this screen is protected from being switched off.</p>
        <div class="table-wrap" style="max-height:520px;">
          <table class="data-table">
            <thead>
              <tr>
                <th style="position:sticky;left:0;background:var(--color-surface);">Module</th>
                <th v-for="r in roles" :key="r.id" style="text-align:center;font-size:10.5px;">{{ r.label }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in modules" :key="m.id">
                <td class="cell-strong" style="position:sticky;left:0;background:var(--color-surface);">{{ m.label }}</td>
                <td v-for="r in roles" :key="r.id" style="text-align:center;">
                  <input type="checkbox"
                    :checked="store.isModuleEnabledForRole(r.id, m.id)"
                    :disabled="r.id==='admin' && m.id==='adminWorkspace'"
                    @change="store.toggleRbacModule(r.id, m.id)" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>